import { useEffect, useMemo, useState } from 'react'
import { Checkbox } from 'app/components/ui/checkbox'
import { Field, FieldGroup, FieldLabel } from 'app/components/ui/field'
import { cn } from 'app/lib/utils'
import { Option } from './SelectAll'

const EMPTY_OPTIONS: Option[] = []

type Props = {
  initialOptions?: Option[]
  showSelectAll?: boolean
  disabled?: boolean
  containerClassName?: string
  onChange?: (options: Option[]) => void
}

export function SelectAllContent({
  initialOptions = EMPTY_OPTIONS,
  showSelectAll,
  disabled = false,
  containerClassName,
  onChange,
}: Props) {
  const [options, setOptions] = useState<Option[]>(initialOptions)

  // Local options are also changed by the selector controls.
  // oxlint-disable-next-line react/no-deriving-state-in-effects
  useEffect(() => {
    // Intentional synchronization: selector controls also update this local state.
    // oxlint-disable-next-line react/no-deriving-state-in-effects
    setOptions(initialOptions)
  }, [initialOptions])

  const isSelectAll = useMemo(
    () =>
      options.every((option) => {
        if (option.groupOptions && option.groupOptions.length > 0) {
          return option.groupOptions.every((groupOption) => groupOption.checked)
        }
        return option.checked
      }),
    [options],
  )

  const handleSelectAllChange = (checked: boolean) => {
    const updatedOptions = options.map((group) => ({
      ...group,
      checked,
      groupOptions: group.groupOptions?.map((child) => ({ ...child, checked })),
    }))
    setOptions(updatedOptions)
    onChange?.(updatedOptions)
  }

  const handleGroupChange = (checked: boolean, groupName: string) => {
    const updatedOptions = options.map((group: Option) => {
      if (group.name === groupName)
        return {
          ...group,
          checked,
          groupOptions: group.groupOptions?.map((option) => ({ ...option, checked })),
        }
      return group
    })
    setOptions(updatedOptions)
    onChange?.(updatedOptions)
  }

  const handleIndividualOptionChange = (checked: boolean, groupName: string, optionName: string) => {
    const updatedOptions = options.map((group) => {
      if (group.name === groupName) {
        const individualOptions = group.groupOptions?.map((groupOption) => {
          if (groupOption.name === optionName) return { ...groupOption, checked }
          return groupOption
        })
        return {
          ...group,
          checked: checked && individualOptions?.every((opt) => opt.checked),
          groupOptions: individualOptions,
        }
      }
      return group
    })
    setOptions(updatedOptions)
    onChange?.(updatedOptions)
  }

  return (
    <div className={cn('flex w-fit flex-col rounded-lg border border-default bg-surface p-3', containerClassName)}>
      {showSelectAll && (
        <Field orientation="horizontal" className="p-2 text-ui-xs" data-disabled={disabled}>
          <Checkbox
            id="select-all"
            checked={isSelectAll}
            disabled={disabled}
            onCheckedChange={(v) => handleSelectAllChange(v === true)}
          />
          <FieldLabel htmlFor="select-all" className="cursor-pointer font-normal">
            All
          </FieldLabel>
        </Field>
      )}
      {options.map((groupSelection) => {
        const hasGroupOptions = groupSelection.groupOptions && groupSelection.groupOptions.length > 0
        const groupId = `group-${groupSelection.name}`

        return (
          <FieldGroup key={groupSelection.name} className="gap-1 text-default">
            <Field orientation="horizontal" className="p-2 text-ui-xs" data-disabled={disabled}>
              <Checkbox
                id={groupId}
                checked={groupSelection.checked}
                disabled={disabled}
                onCheckedChange={(v) => handleGroupChange(v === true, groupSelection.name)}
              />
              <FieldLabel htmlFor={groupId} className="cursor-pointer font-normal">
                {groupSelection.name}
              </FieldLabel>
            </Field>
            {hasGroupOptions &&
              groupSelection.showIndividualOptions &&
              groupSelection.groupOptions?.map((selection) => {
                const name = typeof selection.name === 'function' ? selection.name() : selection.name
                const optionId = `option-${groupSelection.name}-${name}`
                return (
                  <Field
                    key={selection.value}
                    orientation="horizontal"
                    className="py-1 pl-9 pr-3 text-ui-xs"
                    data-disabled={disabled}
                    data-testid={`select-option-${groupSelection.name}-${name}`}
                  >
                    <Checkbox
                      id={optionId}
                      checked={selection.checked}
                      disabled={disabled}
                      onCheckedChange={(v) => handleIndividualOptionChange(v === true, groupSelection.name, name)}
                    />
                    <FieldLabel htmlFor={optionId} className="cursor-pointer text-body-xs font-normal leading-normal">
                      {name}
                    </FieldLabel>
                  </Field>
                )
              })}
          </FieldGroup>
        )
      })}
    </div>
  )
}
